/* POUR CONDITIONS — what counts as a shrinkage-crack warning at this plant.
   Evaporation follows ACI 305R (mean wind, not gusts): 0.75 caution, 1.0 take precautions. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudSun, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
  { k: "evap_caution", label: "Evaporation · caution", unit: "kg/m²/h", step: "0.05", help: "Amber warning at or above this drying rate" },
  { k: "evap_high", label: "Evaporation · take precautions", unit: "kg/m²/h", step: "0.05", help: "Red warning — ACI 305R precaution level is 1.0" },
  { k: "temp_c", label: "Air temperature", unit: "°C", step: "1", help: "Warn above this" },
  { k: "humidity_pct", label: "Humidity", unit: "%", step: "1", help: "Warn below this" },
  { k: "wind_kmh", label: "Wind / gusts", unit: "km/h", step: "1", help: "Warn above this. Gusts are reported but never fed into the evaporation maths" },
  { k: "lead_hours", label: "Shout-ahead window", unit: "hours", step: "1", help: "A rough hour later today stays a quiet grey note until it's this close" },
];

export const PourConditionsCard = ({ canEdit = false }) => {
  const [th, setTh] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/weather/thresholds");
      setTh(r.data.thresholds); setDefaults(r.data.defaults);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load pour conditions"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(FIELDS.map((f) => [f.k, parseFloat(th[f.k])]));
      await api.put("/weather/thresholds", body);
      toast.success("Saved — the banner will use these from the next refresh");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  if (!th) return null;

  return (
    <div className="card-industrial p-5" data-testid="pour-conditions-card">
      <div className="flex items-center gap-2">
        <CloudSun className="w-4 h-4 text-primary-orange" />
        <div className="label-tag">Pour Conditions · Shrinkage-Crack Warning</div>
      </div>
      <p className="text-xs text-muted-industrial mt-2 max-w-2xl">
        Evaporation is worked out the ACI 305R way from air temperature, humidity and <span className="text-ink">mean</span> wind —
        gusts are shown on the banner but never drive the number. Any one of these crossing its line raises the warning.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {FIELDS.map((f) => (
          <label key={f.k} className="block">
            <span className="label-tag">{f.label} <span className="text-muted-industrial">({f.unit})</span></span>
            <input
              type="number" step={f.step} disabled={!canEdit}
              data-testid={`pour-${f.k}`}
              value={th[f.k]}
              onChange={(e) => setTh({ ...th, [f.k]: e.target.value })}
              className="w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2 disabled:opacity-60"
            />
            <span className="block text-[10px] text-muted-industrial mt-1 leading-snug">{f.help}</span>
          </label>
        ))}
      </div>

      {canEdit && (
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={save} disabled={busy} data-testid="pour-save"
            className="bg-primary-orange text-black font-black uppercase tracking-widest text-[11px] px-4 py-2 disabled:opacity-50">
            {busy ? "Saving…" : "Save thresholds"}
          </button>
          <button type="button" onClick={() => setTh({ ...defaults })} data-testid="pour-reset"
            className="mono text-[10px] uppercase tracking-widest border border-industrial px-4 py-2 text-muted-industrial hover:text-ink inline-flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Back to ACI defaults
          </button>
        </div>
      )}
    </div>
  );
};
